import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../App';
import { suggestBiblePassage } from '../services/geminiService';
import { BibleVersion, Sermon } from '../types';
import { 
    Save, BookOpen, Clock, ChevronRight, Search, Plus, 
    Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, 
    List, ListOrdered, Type, Palette, X, Loader2, Copy, Printer, Check, Trash2
} from 'lucide-react';

const SermonBuilder: React.FC = () => {
  const { currentUser, addSermon, sermons, lang } = useAppContext();
  const [topic, setTopic] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  
  // Bible Tool State
  const [bibleQuery, setBibleQuery] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<BibleVersion>(BibleVersion.NIV);
  const [foundVerse, setFoundVerse] = useState<{reference: string, text: string} | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Save State
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search State for Sidebar
  const [sermonSearch, setSermonSearch] = useState('');

  // User's Sermon History (Filtered from global list)
  const mySermons = sermons
    .filter(s => s.churchId === currentUser?.churchId)
    .filter(s => {
        if (!sermonSearch) return true;
        const lowerQ = sermonSearch.toLowerCase();
        return s.title.toLowerCase().includes(lowerQ) || 
               (s.description && s.description.toLowerCase().includes(lowerQ));
    });

  // Colors for text
  const textColors = [
      { name: 'Black', value: '#000000' },
      { name: 'Gray', value: '#4B5563' },
      { name: 'Red', value: '#DC2626' },
      { name: 'Blue', value: '#2563EB' },
      { name: 'Green', value: '#059669' },
      { name: 'Purple', value: '#7C3AED' },
  ];

  // --- Rich Text Commands ---
  const execCmd = (command: string, value: string | undefined = undefined) => {
      // Ensure editor has focus before executing command to prevent applying to body
      if (editorRef.current) {
          if (document.activeElement !== editorRef.current) {
              editorRef.current.focus();
          }
          document.execCommand(command, false, value);
          editorRef.current.focus();
      }
  };

  const handleBibleSearch = async () => {
      if (!bibleQuery) return;
      setIsSearching(true);
      const result = await suggestBiblePassage(bibleQuery, selectedVersion, lang);
      setFoundVerse(result);
      setIsSearching(false);
  };

  const insertVerse = () => {
      if (foundVerse && editorRef.current) {
          editorRef.current.focus();
          
          const htmlToInsert = `
            <br/>
            <blockquote style="border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; color: #4b5563; font-style: italic; background-color: #f9fafb; padding: 1rem; border-radius: 0.25rem;">
                "${foundVerse.text}"
                <br/>
                <strong style="color: #1f2937; font-style: normal; font-size: 0.875rem; margin-top: 0.5rem; display: block;">— ${foundVerse.reference} (${selectedVersion})</strong>
            </blockquote>
            <br/>
          `;
          
          document.execCommand('insertHTML', false, htmlToInsert);
          
          // Clear search after insert
          setFoundVerse(null);
          setBibleQuery('');
      }
  };

  const handleCopy = () => {
      if (editorRef.current) {
          const content = editorRef.current.innerHTML;
          // Create a temporary hidden container to copy HTML properly
          const tempDiv = document.createElement('div');
          tempDiv.contentEditable = "true";
          tempDiv.innerHTML = content;
          document.body.appendChild(tempDiv);
          tempDiv.focus();
          document.execCommand('selectAll');
          document.execCommand('copy');
          document.body.removeChild(tempDiv);
          
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
      }
  };

  const handlePrint = () => {
      if (editorRef.current) {
          const content = editorRef.current.innerHTML;
          const printWindow = window.open('', '', 'height=600,width=800');
          if (printWindow) {
              printWindow.document.write(`
                <html>
                    <head>
                        <title>${topic || 'Sermon'}</title>
                        <style>
                            body { font-family: serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; color: #1f2937; }
                            h1 { font-family: sans-serif; text-align: center; border-bottom: 2px solid #eee; padding-bottom: 20px; }
                            blockquote { border-left: 4px solid #ccc; margin: 20px 0; padding-left: 20px; color: #555; background-color: #f9f9f9; padding: 15px; }
                            ul { list-style-type: disc; margin: 15px 0; padding-left: 40px; }
                            ol { list-style-type: decimal; margin: 15px 0; padding-left: 40px; }
                            li { margin-bottom: 5px; }
                        </style>
                    </head>
                    <body>
                        <h1>${topic || 'Untitled Sermon'}</h1>
                        ${content}
                    </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
          }
      }
  };

  const handleSave = async () => {
      if (!topic.trim() && !editorContent.trim()) {
          alert("Please enter a title or content before saving.");
          return;
      }
      if (!currentUser?.churchId) {
          alert("You must be associated with a church to save sermons.");
          return;
      }

      setIsSaving(true);
      
      try {
          const newSermon: Sermon = {
              id: Date.now().toString(), // Will be updated by DB
              churchId: currentUser.churchId,
              title: topic || 'Untitled Sermon',
              preacher: currentUser.name,
              date: new Date().toISOString(),
              description: editorContent, // Storing HTML in description
              videoUrl: '' 
          };

          await addSermon(newSermon);
          
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
      } catch (e) {
          console.error("Failed to save sermon", e);
          alert("Failed to save sermon. Please try again.");
      } finally {
          setIsSaving(false);
      }
  };

  const loadSermon = (sermon: Sermon) => {
      setTopic(sermon.title);
      setEditorContent(sermon.description || '');
      if (editorRef.current) {
          editorRef.current.innerHTML = sermon.description || '';
      }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-100px)]">
        {/* Custom Styles for Editor Content */}
        <style>{`
            .sermon-editor ul { 
                list-style-type: disc !important; 
                padding-left: 2rem !important; 
                margin: 1rem 0 !important;
                display: block !important;
            }
            .sermon-editor ol { 
                list-style-type: decimal !important; 
                padding-left: 2rem !important; 
                margin: 1rem 0 !important;
                display: block !important;
            }
            .sermon-editor li { 
                margin-bottom: 0.25rem !important; 
                display: list-item !important;
            }
            .sermon-editor[contenteditable]:empty::before {
                content: attr(data-placeholder);
                color: #9ca3af;
                font-style: italic;
            }
        `}</style>

        {/* Main Editor Area */}
        <div className="lg:col-span-3 flex flex-col gap-4 h-full">
            <div className="bg-white rounded-lg shadow flex-1 flex flex-col overflow-hidden border border-gray-200">
                
                {/* 1. Header / Title */}
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-4 justify-between items-center shrink-0">
                    <div className="flex-1 min-w-[200px]">
                        <input 
                            type="text" 
                            className="w-full bg-transparent border-none text-2xl font-bold text-gray-900 placeholder-gray-400 focus:ring-0 px-0"
                            placeholder="Untitled Sermon..."
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleCopy} className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-lg font-medium border border-gray-300 hover:bg-gray-100 flex items-center gap-2 text-sm transition" title="Copy to Clipboard">
                            {copySuccess ? <Check className="w-4 h-4 text-green-600"/> : <Copy className="w-4 h-4" />}
                            {copySuccess ? "Copied!" : "Copy"}
                        </button>
                        <button onClick={handlePrint} className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-lg font-medium border border-gray-300 hover:bg-gray-100 flex items-center gap-2 text-sm transition" title="Print">
                            <Printer className="w-4 h-4" /> Print
                        </button>
                        <button 
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`px-4 py-2 rounded-lg font-bold flex items-center gap-2 text-sm shadow-sm transition text-white ${saveSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : saveSuccess ? <Check className="w-4 h-4"/> : <Save className="w-4 h-4" />}
                            {isSaving ? "Saving..." : saveSuccess ? "Saved" : "Save"}
                        </button>
                    </div>
                </div>

                {/* 2. Bible Search Tool (Moved to Top) */}
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 shrink-0">
                    <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-blue-700" />
                        <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Bible Reference Tool</span>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2">
                        <select 
                            className="w-24 bg-white border border-blue-200 rounded px-2 py-2 text-sm focus:outline-none focus:border-blue-500 text-blue-900 font-medium"
                            value={selectedVersion}
                            onChange={(e) => setSelectedVersion(e.target.value as BibleVersion)}
                        >
                            {Object.values(BibleVersion).map(v => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                        <div className="flex-1 flex gap-2">
                            <input 
                                type="text" 
                                className="flex-1 bg-white border border-blue-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 placeholder-blue-300"
                                placeholder="Search for a verse (e.g. 'John 3:16' or 'Faith')"
                                value={bibleQuery}
                                onChange={(e) => setBibleQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleBibleSearch()}
                            />
                            <button 
                                onClick={handleBibleSearch}
                                disabled={isSearching || !bibleQuery}
                                className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                            >
                                {isSearching ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4" />}
                                Find
                            </button>
                        </div>
                    </div>
                    
                    {/* Search Result Preview */}
                    {foundVerse && (
                        <div className="mt-3 bg-white border border-blue-200 rounded-lg p-3 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between shadow-sm animate-fadeIn">
                            <div className="flex-1">
                                <span className="font-bold text-blue-800 text-sm mr-2">{foundVerse.reference}</span>
                                <span className="text-gray-600 text-sm italic">"{foundVerse.text}"</span>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button 
                                    onClick={() => setFoundVerse(null)}
                                    className="text-gray-400 hover:text-gray-600 px-2 py-1 text-xs font-medium"
                                >
                                    Dismiss
                                </button>
                                <button 
                                    onClick={insertVerse}
                                    className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-green-700 flex items-center gap-1 shadow-sm"
                                >
                                    <Plus className="w-3 h-3" /> Insert Verse
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 3. Formatting Toolbar */}
                <div className="px-4 py-2 border-b border-gray-200 bg-white flex flex-wrap gap-2 items-center shrink-0 sticky top-0 z-10">
                    {/* Font & Size */}
                    <div className="flex items-center border-r border-gray-200 pr-2 gap-1">
                        <select 
                            className="text-sm border border-gray-300 rounded px-1 py-1 w-24 focus:outline-none focus:border-blue-500"
                            onChange={(e) => execCmd('fontName', e.target.value)}
                        >
                            <option value="Arial">Arial</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Times New Roman">Times</option>
                            <option value="Verdana">Verdana</option>
                        </select>
                        <select 
                            className="text-sm border border-gray-300 rounded px-1 py-1 w-16 focus:outline-none focus:border-blue-500"
                            onChange={(e) => execCmd('fontSize', e.target.value)}
                        >
                            <option value="3">12pt</option>
                            <option value="4">14pt</option>
                            <option value="5">18pt</option>
                            <option value="6">24pt</option>
                            <option value="7">36pt</option>
                        </select>
                    </div>

                    {/* Styling */}
                    <div className="flex items-center border-r border-gray-200 pr-2 gap-1">
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('bold'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 hover:text-black transition" title="Bold"><Bold className="w-4 h-4"/></button>
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('italic'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 hover:text-black transition" title="Italic"><Italic className="w-4 h-4"/></button>
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('underline'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700 hover:text-black transition" title="Underline"><Underline className="w-4 h-4"/></button>
                        
                        {/* Text Color Dropdown */}
                        <div className="relative">
                            <button 
                                onMouseDown={(e) => { e.preventDefault(); setShowColorPalette(!showColorPalette); }}
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-700 hover:text-black transition flex items-center gap-1"
                                title="Text Color"
                            >
                                <Palette className="w-4 h-4" />
                            </button>
                            
                            {showColorPalette && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-xl rounded-lg p-2 z-50 grid grid-cols-4 gap-1 w-40">
                                    {textColors.map(color => (
                                        <button
                                            key={color.value}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                execCmd('foreColor', color.value);
                                                setShowColorPalette(false);
                                            }}
                                            className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition shadow-sm"
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                        />
                                    ))}
                                    {/* Custom Color Input */}
                                    <label className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition shadow-sm flex items-center justify-center cursor-pointer bg-gradient-to-br from-red-500 via-green-500 to-blue-500">
                                        <input 
                                            type="color" 
                                            className="opacity-0 w-full h-full cursor-pointer absolute"
                                            onChange={(e) => {
                                                execCmd('foreColor', e.target.value);
                                                setShowColorPalette(false);
                                            }}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Alignment */}
                    <div className="flex items-center border-r border-gray-200 pr-2 gap-1">
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('justifyLeft'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700" title="Align Left"><AlignLeft className="w-4 h-4"/></button>
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('justifyCenter'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700" title="Align Center"><AlignCenter className="w-4 h-4"/></button>
                        <button onMouseDown={(e) => { e.preventDefault(); execCmd('justifyRight'); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-700" title="Align Right"><AlignRight className="w-4 h-4"/></button>
                    </div>

                    {/* Lists */}
                    <div className="flex items-center gap-1">
                        <button 
                            onMouseDown={(e) => { e.preventDefault(); execCmd('insertUnorderedList'); }} 
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-700" 
                            title="Bullet List"
                        >
                            <List className="w-4 h-4"/>
                        </button>
                        <button 
                            onMouseDown={(e) => { e.preventDefault(); execCmd('insertOrderedList'); }} 
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-700" 
                            title="Numbered List"
                        >
                            <ListOrdered className="w-4 h-4"/>
                        </button>
                    </div>
                </div>

                {/* 4. Editor Area */}
                <div 
                    ref={editorRef}
                    contentEditable
                    className="flex-1 p-8 overflow-y-auto outline-none sermon-editor font-serif text-lg text-gray-800 leading-relaxed cursor-text"
                    onInput={(e) => setEditorContent(e.currentTarget.innerHTML)}
                    style={{ minHeight: '300px' }}
                    data-placeholder="Start writing your sermon..."
                />
            </div>
        </div>

        {/* Sidebar - History */}
        <div className="lg:col-span-1 space-y-6 flex flex-col h-full overflow-hidden">
            <div className="bg-white rounded-lg shadow border border-gray-100 flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50 space-y-3">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-gray-500"/> Sermon Archive
                    </h3>
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Search sermons..." 
                            className="w-full pl-8 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                            value={sermonSearch}
                            onChange={(e) => setSermonSearch(e.target.value)}
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {mySermons.length > 0 ? (
                        <div className="space-y-1">
                            {mySermons.map((item) => (
                                <div 
                                    key={item.id} 
                                    onClick={() => loadSermon(item)}
                                    className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer group transition"
                                >
                                    <div className="min-w-0">
                                        <h4 className="font-medium text-gray-900 text-sm group-hover:text-blue-600 truncate">{item.title}</h4>
                                        <p className="text-xs text-gray-500">{new Date(item.date).toLocaleDateString()}</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 shrink-0" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            {sermonSearch ? "No sermons found." : "No sermons saved yet."}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );
};

export default SermonBuilder;